import React, { useCallback, useEffect, useState } from 'react';

/**
 * HorizontalResizeHandle — draggable handle for resizing a panel's HEIGHT.
 *
 * Mirrors ResizeHandle.jsx but operates on the y-axis. Used to resize the
 * collapsible admin section at the bottom of the sidebar. The handle sits at
 * the TOP of the resizable panel; dragging it UP grows the panel, dragging
 * it DOWN shrinks the panel.
 *
 * @param {function} onResize - Callback with new height in px (clamped to [minHeight, maxHeight])
 * @param {number}   currentHeight - The current panel height (px) so drag math is relative
 * @param {number}   minHeight - Minimum panel height (default 120)
 * @param {number}   maxHeight - Maximum panel height (default 600)
 */
function HorizontalResizeHandle({ onResize, currentHeight = 240, minHeight = 120, maxHeight = 600 }) {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartY(e.clientY);
    setStartHeight(currentHeight);
  }, [currentHeight]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    // Handle is at the TOP of the panel — dragging UP (smaller clientY) grows the panel.
    const diff = startY - e.clientY;
    const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + diff));
    onResize?.(newHeight);
  }, [isDragging, startY, startHeight, minHeight, maxHeight, onResize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`
        relative w-full h-1 cursor-row-resize z-60
        hover:bg-decisiv-blue/50 active:bg-decisiv-blue
        transition-colors shrink-0
        ${isDragging ? 'bg-decisiv-blue' : 'bg-transparent hover:bg-gray-300 dark:hover:bg-gray-600'}
      `}
      title="Drag to resize admin panel"
      aria-label="Resize admin panel"
      role="separator"
    />
  );
}

export default HorizontalResizeHandle;
